import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { Plus, Send, Eye, Receipt } from "lucide-react";
import financeService from "../../services/financeService";
import { toast } from "react-hot-toast";

const ImprestManagement = () => {
  const [loading, setLoading] = useState(false);
  const [imprestTransactions, setImprestTransactions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [selectedImprest, setSelectedImprest] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    description: "",
    amount: "",
    beneficiary: "",
    date: new Date().toISOString().split("T")[0],
    receipt_number: "",
  });

  useEffect(() => {
    loadImprestTransactions();
  }, []);

  const loadImprestTransactions = async () => {
    try {
      setLoading(true);
      const response = await financeService.getTransactions({
        status: "posted",
        limit: 100,
      });
      
      const transactions = response?.data?.data?.transactions || [];
      const imprestTxns = transactions.filter(t =>
        !t.imprest_retired_at &&
        (t.DebitAccount?.name?.toLowerCase().includes("imprest") ||
        t.DebitAccount?.name?.toLowerCase().includes("petty cash"))
      );
      
      setImprestTransactions(imprestTxns);
      
      if (imprestTxns.length > 0) {
        loadExpenses(imprestTxns[0].id);
        setSelectedImprest(imprestTxns[0]);
      }
    } catch (error) {
      console.error("Load imprest error:", error);
      toast.error("Failed to load imprest transactions");
    } finally {
      setLoading(false);
    }
  };

  const loadExpenses = async (transactionId) => {
    try {
      const response = await financeService.getImprestExpenses(transactionId);
      setExpenses(response?.data?.data?.expenses || []);
    } catch (error) {
      console.error("Load expenses error:", error);
      setExpenses([]);
    }
  };

  const handleAddExpense = async () => {
    if (!expenseForm.description || !expenseForm.amount || !expenseForm.beneficiary) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);
      await financeService.createImprestExpense(selectedImprest.id, expenseForm);
      toast.success("Expense recorded successfully");
      setShowExpenseDialog(false);
      setExpenseForm({
        description: "",
        amount: "",
        beneficiary: "",
        date: new Date().toISOString().split("T")[0],
        receipt_number: "",
      });
      loadExpenses(selectedImprest.id);
    } catch (error) {
      toast.error("Failed to record expense");
      console.error("Add expense error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRetirement = async () => {
    if (!selectedImprest) return;

    try {
      setLoading(true);
      await financeService.submitImprestRetirement(selectedImprest.id);
      toast.success("Retirement report submitted to GMD");
      loadImprestTransactions();
    } catch (error) {
      toast.error("Failed to submit retirement");
      console.error("Submit retirement error:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalExpenses = expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
  const balance = selectedImprest ? parseFloat(selectedImprest.amount) - totalExpenses : 0;

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Imprest Management</h1>
          <p className="text-gray-600 mt-1">Track petty cash expenses and submit retirement</p>
        </div>
      </div>

      {selectedImprest && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">Imprest Amount</p>
                <p className="text-2xl font-bold text-blue-600">
                  {financeService.formatCurrency(selectedImprest.amount)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">Total Expenses</p>
                <p className="text-2xl font-bold text-red-600">
                  {financeService.formatCurrency(totalExpenses)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">Balance</p>
                <p className="text-2xl font-bold text-green-600">
                  {financeService.formatCurrency(balance)}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Expenses ({expenses.length})</CardTitle>
                <div className="flex gap-2">
                  <Button onClick={() => setShowExpenseDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Expense
                  </Button>
                  <Button 
                    onClick={handleSubmitRetirement}
                    className="bg-green-600 hover:bg-green-700"
                    disabled={expenses.length === 0}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit to GMD
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {expenses.map((expense) => (
                  <Card key={expense.id}>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium">{expense.description}</p>
                          <p className="text-sm text-gray-600">Paid to: {expense.beneficiary}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(expense.date).toLocaleDateString()}
                            {expense.receipt_number && ` • Receipt: ${expense.receipt_number}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-600">
                            {financeService.formatCurrency(expense.amount)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {expenses.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No expenses recorded yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!selectedImprest && !loading && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No active imprest found</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Description *</Label>
              <Textarea
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                placeholder="What was purchased?"
                rows={2}
              />
            </div>

            <div>
              <Label>Beneficiary *</Label>
              <Input
                value={expenseForm.beneficiary}
                onChange={(e) => setExpenseForm({ ...expenseForm, beneficiary: e.target.value })}
                placeholder="Who received the payment?"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Receipt Number</Label>
              <Input
                value={expenseForm.receipt_number}
                onChange={(e) => setExpenseForm({ ...expenseForm, receipt_number: e.target.value })}
                placeholder="Optional"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowExpenseDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddExpense} disabled={loading}>
                {loading ? "Recording..." : "Record Expense"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImprestManagement;
